import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import type { MonthlyStats, TransactionSummary } from '../types';

interface ChartViewProps {
  monthlyStats: MonthlyStats[];
  summaries: TransactionSummary[];
}

export default function ChartView({ monthlyStats, summaries }: ChartViewProps) {
  // 월별 거래 추이 차트 데이터
  const monthlyChartData = [...monthlyStats]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map(m => ({
      month: m.month,
      '거래건수': m.count,
      '평균가(만원)': m.avgPrice,
      '최저가(만원)': m.minPrice,
      '최고가(만원)': m.maxPrice,
    }));

  // 아파트별 평당가 비교 차트 데이터 (상위 10개)
  const topApartments = [...summaries]
    .sort((a, b) => b.avgPricePerPyeong - a.avgPricePerPyeong)
    .slice(0, 10)
    .map(s => ({
      name: s.aptName.length > 8 ? s.aptName.substring(0, 8) + '...' : s.aptName,
      fullName: s.aptName,
      '평당가(만원)': s.avgPricePerPyeong,
      '거래건수': s.transactionCount,
    }));

  return (
    <div className="space-y-8">
      {/* 월별 거래 추이 */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">📈 월별 거래 추이</h2>
        {monthlyChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="거래건수" 
                stroke="#3b82f6" 
                strokeWidth={2}
                name="거래건수"
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="평균가(만원)" 
                stroke="#10b981" 
                strokeWidth={2}
                name="평균가(만원)"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            차트 데이터가 없습니다.
          </div>
        )}
      </div>

      {/* 아파트별 평당가 비교 */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">🏠 아파트별 평당가 비교 (상위 10개)</h2>
        {topApartments.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={topApartments} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={100} />
              <Tooltip 
                formatter={(value, name) => [value, name]}
                labelFormatter={(label) => {
                  const apt = topApartments.find(a => a.name === label);
                  return apt?.fullName || label;
                }}
              />
              <Legend />
              <Bar dataKey="평당가(만원)" fill="#3b82f6" name="평당가(만원)" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-8 text-gray-500">
            차트 데이터가 없습니다.
          </div>
        )}
      </div>

      {/* 가격 분포 */}
      {summaries.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">💰 가격 분포</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={summaries.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="aptName" angle={-45} textAnchor="end" height={100} interval={0} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="minPrice" fill="#10b981" name="최저가(만원)" />
              <Bar dataKey="avgPrice" fill="#3b82f6" name="평균가(만원)" />
              <Bar dataKey="maxPrice" fill="#ef4444" name="최고가(만원)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}